import { differenceInMonths } from 'date-fns';
import type { Membership, Redemption } from './types';

export const getCurrentMonthYear = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export const getUsedBenefitsThisMonth = (
  redemptions: Redemption[],
  benefitType: string
): number => {
  const currentMonth = getCurrentMonthYear();
  return redemptions
    .filter(r => r.month_year === currentMonth && r.benefit_type === benefitType)
    .reduce((acc, r) => acc + r.quantity, 0);
};

export const calculateRemainingMonths = (membership: Membership | null): number => {
  if (!membership) return 0;
  const endDate = new Date(membership.end_date);
  const today = new Date();
  const diffTime = endDate.getTime() - today.getTime();
  const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30));
  return Math.max(0, diffMonths);
};

export interface NearExpirationResult {
  isExpiring: boolean;
  daysLeft: number;
  urgency: 'warning' | 'critical' | 'none';
}

export const isNearExpiration = (membership: Membership | null): NearExpirationResult => {
  if (!membership) return { isExpiring: false, daysLeft: 0, urgency: 'none' };
  
  const endDate = new Date(membership.end_date);
  const today = new Date();
  const daysLeft = Math.max(0, Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
  
  let warningThreshold: number;
  let criticalThreshold: number;
  
  switch (membership.billing_period) {
    case 'mensual':
      warningThreshold = 7;
      criticalThreshold = 3;
      break;
    case 'anual':
      warningThreshold = 30;
      criticalThreshold = 14;
      break;
    default:
      warningThreshold = 7;
      criticalThreshold = 3;
  }
  
  if (daysLeft <= criticalThreshold) {
    return { isExpiring: true, daysLeft, urgency: 'critical' };
  } else if (daysLeft <= warningThreshold) {
    return { isExpiring: true, daysLeft, urgency: 'warning' };
  }
  
  return { isExpiring: false, daysLeft, urgency: 'none' };
};

export interface RemainingTimeResult {
  value: number;
  unit: string;
  progress: number;
}

export const calculateRemainingTime = (membership: Membership | null): RemainingTimeResult => {
  if (!membership) return { value: 0, unit: 'días', progress: 0 };
  
  const endDate = new Date(membership.end_date);
  const startDate = new Date(membership.start_date);
  const today = new Date();
  
  const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  const remainingDays = Math.max(0, Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
  
  const progress = Math.min(100, (remainingDays / totalDays) * 100);
  
  if (membership.billing_period === 'mensual') {
    return { value: remainingDays, unit: 'días', progress };
  } else {
    // anual
    const months = differenceInMonths(endDate, today);
    return { value: Math.max(1, months), unit: 'meses', progress };
  }
};

export const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('es-CR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

export const formatShortDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('es-CR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};
